/**
 * Label + panel-grouping derivation for the Refresh-step candidate picker.
 *
 * Pure, so the picker's two easy-to-regress behaviours (manufacturer rendered
 * once; every candidate lands in exactly one panel) are testable without a
 * DOM or a query client.
 */

export type RefreshCandidateLike = {
  skuId: string;
  make: string | null;
  model: string | null;
  /**
   * Manufacturer-free name computed server-side by `stripManufacturerPrefix`
   * (SIL-180). Preferred over `model`, which repeats the manufacturer.
   */
  displayName: string | null;
};

const OTHER_LINE = 'Other';

/**
 * Pair delimiter for the (manufacturer, line) group key. ASCII Unit Separator
 * is a control character, so it cannot occur in a manufacturer name or in a
 * derived line — which makes the pair -> key mapping injective and keeps two
 * different pairs from ever collapsing into one panel.
 */
const KEY_SEP = '\u001F';

function normalizeSpace(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/\s+/g, ' ');
}

/**
 * The model half of a row label, with the manufacturer already absent.
 *
 * The API strips the manufacturer off `model_name` server-side
 * (`stripManufacturerPrefix`) and sends the result as `displayName`, so there
 * is no client-side stripping here. `model` (`model_name`, which DOES embed
 * the manufacturer) is only the last-resort fallback for a row the API sent no
 * `displayName` for; a duplicated manufacturer there beats a blank row.
 */
export function candidateModelName(cand: RefreshCandidateLike): string {
  return normalizeSpace(cand.displayName) || normalizeSpace(cand.model);
}

/** Row label: manufacturer once, then the manufacturer-free model name. */
export function candidateLabel(cand: RefreshCandidateLike): string {
  const make = normalizeSpace(cand.make);
  const model = candidateModelName(cand);
  return [make, model].filter(Boolean).join(' ') || cand.skuId;
}

/**
 * ponytail: model line is derived from the first token of the
 * manufacturer-free display name — i.e. of `model_name` with the manufacturer
 * already stripped off ("POWEREDGE R750XA" -> POWEREDGE, "EPYC 9684X" ->
 * EPYC). Ceiling: it is a string heuristic over a free-text name, so
 * vendor-prefixed names group imperfectly ("HPE CRAY XD675" -> HPE, not CRAY),
 * a row whose `model_name` does not start with its manufacturer groups on the
 * manufacturer token instead, and any upstream `model_name` edit silently
 * reshuffles the panels. The line alone is NOT a panel identity — panels are
 * keyed on the (manufacturer, line) pair, so two manufacturers sharing a line
 * token get one panel each and every header names exactly one manufacturer.
 * Upgrade path: add a real `model_line` column to `sku`, populate it in the
 * ingestion promoter alongside `display_name`, expose it on
 * /refresh-candidates, and reduce this function to a fallback for rows that
 * still lack one.
 */
export function deriveModelLine(cand: RefreshCandidateLike): string {
  const first = candidateModelName(cand).split(' ')[0] ?? '';
  // Trailing punctuation alone ("-", ",") is not a line name.
  const line = first.replace(/[^\p{L}\p{N}+/-]/gu, '');
  return line ? line.toUpperCase() : OTHER_LINE;
}

/** Manufacturer as stored (title-case), or '' for a row that carries none. */
function candidateMake(cand: RefreshCandidateLike): string {
  return normalizeSpace(cand.make);
}

/**
 * Panel header: `MANUFACTURER | MODEL`, both segments upper-cased so the two
 * halves read as one unit (the manufacturer is stored title-case, "Dell
 * Technologies"). A row with no manufacturer degrades to the line alone —
 * never a dangling " | ".
 */
export function candidateGroupHeading(make: string, line: string): string {
  const left = normalizeSpace(make).toUpperCase();
  const right = line.toUpperCase();
  return left ? `${left} | ${right}` : right;
}

export type CandidateGroup<T extends RefreshCandidateLike> = {
  /**
   * Stable panel identity: the (manufacturer, line) pair. Drives the React
   * key and the collapse-toggle map, so it must not change when the list is
   * filtered or re-sorted.
   */
  key: string;
  /** Manufacturer as stored (title-case), '' when the rows carry none. */
  make: string;
  /** Derived model line, or "Other" for rows with no usable name. */
  line: string;
  /** Rendered header — see `candidateGroupHeading`. */
  heading: string;
  candidates: T[];
};

/** Sorts `a` before `b` alphabetically, except `pinLast` which always trails. */
function comparePinningLast(a: string, b: string, pinLast: string): number {
  if (a === b) return 0;
  if (a === pinLast) return 1;
  if (b === pinLast) return -1;
  return a.localeCompare(b);
}

/**
 * Groups candidates into one panel per (manufacturer, model line) pair.
 *
 * The pair — not the line alone — is the key on purpose: if two manufacturers
 * ever ship the same line token, keying on the line would force one panel to
 * print a manufacturer that is wrong for some of its own rows. Keying on the
 * pair makes "every panel has exactly one manufacturer" true by construction.
 *
 * Every candidate lands in exactly one group — a row with no usable name
 * falls into "Other" rather than vanishing from the picker. Groups sort by
 * manufacturer, then by line within it, with the unknowns ("" manufacturer,
 * "Other" line) pinned last at their level.
 */
export function groupCandidatesByMakeAndLine<T extends RefreshCandidateLike>(
  candidates: readonly T[],
): CandidateGroup<T>[] {
  const byPair = new Map<string, CandidateGroup<T>>();
  for (const cand of candidates) {
    const make = candidateMake(cand);
    const line = deriveModelLine(cand);
    const key = `${make}${KEY_SEP}${line}`;
    const bucket = byPair.get(key);
    if (bucket) bucket.candidates.push(cand);
    else
      byPair.set(key, {
        key,
        make,
        line,
        heading: candidateGroupHeading(make, line),
        candidates: [cand],
      });
  }
  return Array.from(byPair.values()).sort(
    (a, b) =>
      comparePinningLast(a.make, b.make, '') || comparePinningLast(a.line, b.line, OTHER_LINE),
  );
}
